import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, TrendingUp, TrendingDown, Home, DollarSign, FileText, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SimulationResults({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSimulation();
  }, [id]);

  const loadSimulation = async () => {
    try {
      const response = await axiosInstance.get(`${API}/simulations/${id}`, {
        withCredentials: true
      });
      setSimulation(response.data);
    } catch (error) {
      console.error('Error loading simulation:', error);
      toast.error('Simulation introuvable');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    // Open PDF export page in new tab (browser will handle print to PDF)
    window.open(`${API}/simulations/${id}/export-pdf`, '_blank');
    toast.success('Page d\'impression ouverte');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-cyan-500 border-t-transparent"></div>
          <p className="mt-4 text-cyan-400 font-['Rajdhani']">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!simulation) return null;

  // Prepare chart data for cash flow comparison
  const cashflowData = [
    {
      name: 'Location traditionnelle',
      'Cash-flow': simulation.cashflow_traditional,
    },
    {
      name: 'Airbnb',
      'Cash-flow': simulation.cashflow_airbnb,
    }
  ];

  // Prepare data for value appreciation chart
  const years = [];
  const appreciationRate = simulation.avg_inflation_10y / 100;
  for (let year = 0; year <= simulation.loan_duration; year += Math.ceil(simulation.loan_duration / 10)) {
    years.push({
      year: `Année ${year}`,
      value: Math.round(simulation.property_price * Math.pow(1 + appreciationRate, year))
    });
  }

  return (
    <div className="min-h-screen bg-[#030305] relative">
      <div className="cyber-grid-bg"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 backdrop-blur-xl bg-black/20">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Tableau de bord
              </Button>
              <h1 className="text-xl font-bold font-['Orbitron'] tracking-wider text-white">
                RÉSULTATS DE <span className="text-cyan-400">SIMULATION</span>
              </h1>
              <Button
                onClick={handleDownloadPDF}
                data-testid="download-pdf-button"
                variant="outline"
                className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10"
              >
                <FileText className="mr-2 h-4 w-4" />
                PDF
              </Button>
              <Button
                onClick={() => window.open(`${API}/simulations/${id}/export-excel`, '_blank')}
                data-testid="download-excel-button"
                className="bg-green-600 hover:bg-green-500 text-white font-bold"
              >
                <Download className="mr-2 h-4 w-4" />
                Excel
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-6 py-12 max-w-7xl">
          {/* Property Info */}
          <div className="glass-panel p-8 rounded-lg mb-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold font-['Orbitron'] text-white mb-2">
                  {simulation.location}
                </h2>
                <p className="text-slate-400">
                  {simulation.surface_m2} m² • {simulation.nb_rooms} pièces
                </p>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-400">Prix d'acquisition</div>
                <div className="text-2xl font-bold text-cyan-400">
                  {simulation.property_price.toLocaleString()} €
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded">
                <div className="text-sm text-slate-400 mb-1">Travaux</div>
                <div className="text-lg font-semibold text-white">
                  {simulation.renovation_cost.toLocaleString()} €
                </div>
              </div>
              <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded">
                <div className="text-sm text-slate-400 mb-1">Type de prêt</div>
                <div className="text-lg font-semibold text-white">{simulation.loan_type}%</div>
              </div>
              <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded">
                <div className="text-sm text-slate-400 mb-1">Taux d'intérêt</div>
                <div className="text-lg font-semibold text-white">{simulation.bank_rate}%</div>
              </div>
              <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded">
                <div className="text-sm text-slate-400 mb-1">Durée</div>
                <div className="text-lg font-semibold text-white">{simulation.loan_duration} ans</div>
              </div>
            </div>
          </div>

          {/* Loan Details */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="glass-panel p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <DollarSign className="h-6 w-6 text-cyan-400" />
                <h3 className="text-lg font-semibold text-white">Montant emprunté</h3>
              </div>
              <div className="text-3xl font-bold text-cyan-400">
                {simulation.total_loan.toLocaleString()} €
              </div>
            </div>

            <div className="glass-panel p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <Home className="h-6 w-6 text-cyan-400" />
                <h3 className="text-lg font-semibold text-white">Mensualité</h3>
              </div>
              <div className="text-3xl font-bold text-cyan-400">
                {simulation.monthly_payment.toLocaleString()} €
              </div>
            </div>

            <div className="glass-panel p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <TrendingUp className="h-6 w-6 text-red-400" />
                <h3 className="text-lg font-semibold text-white">Coût du crédit</h3>
              </div>
              <div className="text-3xl font-bold text-red-400">
                {simulation.total_interest.toLocaleString()} €
              </div>
            </div>
          </div>

          {/* Charges Détaillées */}
          <div className="glass-panel p-6 rounded-lg mb-8">
            <h3 className="text-2xl font-bold font-['Rajdhani'] text-white mb-6">
              💰 Détail des Charges
            </h3>
            
            {/* Charges communes */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-cyan-400 mb-3 flex items-center gap-2">
                Charges Propriétaire (tous types de location)
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-slate-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Ces charges sont à payer quel que soit le type de location</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-800/30 rounded-lg">
                  <div className="text-sm text-slate-400 mb-1">Taxe foncière</div>
                  <div className="text-lg font-bold text-white">{simulation.taxe_fonciere_annual.toLocaleString()} € /an</div>
                  <div className="text-xs text-slate-500 mt-1">{(simulation.taxe_fonciere_annual / 12).toFixed(2)} € /mois</div>
                </div>
                <div className="p-4 bg-slate-800/30 rounded-lg">
                  <div className="text-sm text-slate-400 mb-1">Assurance PNO</div>
                  <div className="text-lg font-bold text-white">{simulation.property_insurance_annual.toLocaleString()} € /an</div>
                  <div className="text-xs text-slate-500 mt-1">{(simulation.property_insurance_annual / 12).toFixed(2)} € /mois</div>
                </div>
                <div className="p-4 bg-slate-800/30 rounded-lg">
                  <div className="text-sm text-slate-400 mb-1">Provisions travaux</div>
                  <div className="text-lg font-bold text-white">{simulation.maintenance_reserve_monthly.toLocaleString()} € /mois</div>
                </div>
              </div>
            </div>
            
            {/* Charges spécifiques Airbnb */}
            <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
              <h4 className="text-lg font-semibold text-cyan-400 mb-3 flex items-center gap-2">
                ⚡ Charges Spécifiques Airbnb UNIQUEMENT
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-cyan-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-cyan-900/90 border-cyan-500/50">
                      <p className="font-bold mb-2">⚠️ Différence Importante !</p>
                      <p className="mb-2"><strong>Location traditionnelle :</strong> Le locataire paie l'eau, l'électricité et internet.</p>
                      <p><strong>Airbnb :</strong> C'est VOUS qui payez tout ! C'est pourquoi le cash-flow Airbnb est souvent plus bas que prévu.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </h4>
              <p className="text-sm text-slate-300 mb-4">
                💡 En location traditionnelle, le locataire paie ces charges. En Airbnb, c'est à votre charge !
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Électricité (abonnement)</span>
                    <span className="text-white">{simulation.electricity_subscription_monthly.toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Électricité (consommation)</span>
                    <span className="text-white">{simulation.electricity_consumption_monthly.toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Eau (abonnement)</span>
                    <span className="text-white">{simulation.water_subscription_monthly.toLocaleString()} €</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Eau (consommation)</span>
                    <span className="text-white">{simulation.water_consumption_monthly.toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Internet (box 5G)</span>
                    <span className="text-white">{simulation.internet_monthly.toLocaleString()} €</span>
                  </div>
                  {simulation.conciergerie_monthly > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">🧹 Conciergerie Airbnb</span>
                      <span className="text-white">{simulation.conciergerie_monthly.toLocaleString()} €</span>
                    </div>
                  )}
                  {simulation.linge_monthly > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">🛏️ Frais linge</span>
                      <span className="text-white">{simulation.linge_monthly.toLocaleString()} €</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-bold pt-2 border-t border-cyan-500/30">
                    <span className="text-cyan-300">TOTAL Airbnb /mois</span>
                    <span className="text-cyan-300">{simulation.total_airbnb_charges_monthly.toLocaleString()} €</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Dépenses réelles si liées à un bien */}
            {simulation.property_id && (simulation.total_real_travaux > 0 || simulation.total_real_amenagement > 0) && (
              <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <h4 className="text-lg font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                  📊 Investissement Réel Tracké
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-yellow-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Ces montants sont basés sur les dépenses réelles que vous avez enregistrées pour ce bien. C'est votre investissement RÉEL !</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </h4>
                <p className="text-slate-300 mb-3 text-sm">
                  🎯 Ces montants sont basés sur vos dépenses réelles enregistrées pour ce bien
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-3 bg-yellow-500/5 rounded">
                    <div className="text-sm text-slate-400 mb-1">Travaux réels</div>
                    <div className="text-xl font-bold text-yellow-300">
                      {simulation.total_real_travaux.toLocaleString()} €
                    </div>
                  </div>
                  <div className="p-3 bg-yellow-500/5 rounded">
                    <div className="text-sm text-slate-400 mb-1">Aménagement réel</div>
                    <div className="text-xl font-bold text-yellow-300">
                      {simulation.total_real_amenagement.toLocaleString()} €
                    </div>
                  </div>
                  <div className="p-3 bg-yellow-500/10 rounded border border-yellow-500/30">
                    <div className="text-sm text-slate-400 mb-1">Total investi</div>
                    <div className="text-xl font-bold text-yellow-400">
                      {(simulation.total_real_travaux + simulation.total_real_amenagement).toLocaleString()} €
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>


          {/* Cash Flow Comparison */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Traditional Rental */}
            <div className="glass-panel p-6 rounded-lg">
              <h3 className="text-xl font-bold font-['Rajdhani'] text-white mb-4">
                📋 Location Traditionnelle
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Loyer mensuel</span>
                  <span className="text-white font-semibold">
                    {simulation.monthly_rent_traditional.toLocaleString()} €
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Revenus annuels</span>
                  <span className="text-white font-semibold">
                    {simulation.yearly_rent_traditional.toLocaleString()} €
                  </span>
                </div>
                <div className="flex justify-between border-t border-white/10 pt-3">
                  <span className="text-slate-400 font-semibold">Cash-flow mensuel</span>
                  <span className={`flex items-center gap-2 text-xl font-bold ${
                    simulation.cashflow_traditional > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {simulation.cashflow_traditional > 0 ? (
                      <TrendingUp className="h-5 w-5" />
                    ) : (
                      <TrendingDown className="h-5 w-5" />
                    )}
                    {simulation.cashflow_traditional.toLocaleString()} €
                  </span>
                </div>
              </div>
            </div>

            {/* Airbnb Rental */}
            <div className="glass-panel p-6 rounded-lg">
              <h3 className="text-xl font-bold font-['Rajdhani'] text-white mb-4">
                🏠 Location Airbnb
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Tarif journalier</span>
                  <span className="text-white font-semibold">
                    {simulation.airbnb_daily_rate.toLocaleString()} €
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Revenus mensuels (brut)</span>
                  <span className="text-white font-semibold">
                    {simulation.airbnb_monthly_income.toLocaleString()} €
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Revenus annuels</span>
                  <span className="text-white font-semibold">
                    {simulation.airbnb_yearly_income.toLocaleString()} €
                  </span>
                </div>
                <div className="flex justify-between border-t border-white/10 pt-3">
                  <span className="text-slate-400 font-semibold">Cash-flow mensuel (net 80%)</span>
                  <span className={`flex items-center gap-2 text-xl font-bold ${
                    simulation.cashflow_airbnb > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {simulation.cashflow_airbnb > 0 ? (
                      <TrendingUp className="h-5 w-5" />
                    ) : (
                      <TrendingDown className="h-5 w-5" />
                    )}
                    {simulation.cashflow_airbnb.toLocaleString()} €
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Cash Flow Comparison Chart */}
            <div className="glass-panel p-6 rounded-lg">
              <h3 className="text-xl font-bold font-['Rajdhani'] text-white mb-4">
                Comparaison Cash-flow
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={cashflowData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="#94A3B8" />
                  <YAxis stroke="#94A3B8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      border: '1px solid rgba(0, 240, 255, 0.3)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Bar dataKey="Cash-flow" fill="#00F0FF" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Value Appreciation Chart */}
            <div className="glass-panel p-6 rounded-lg">
              <h3 className="text-xl font-bold font-['Rajdhani'] text-white mb-4">
                Évolution de la valeur ({simulation.avg_inflation_10y}% inflation)
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={years}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="year" stroke="#94A3B8" />
                  <YAxis stroke="#94A3B8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      border: '1px solid rgba(0, 240, 255, 0.3)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value) => `${value.toLocaleString()} €`}
                  />
                  <Line type="monotone" dataKey="value" stroke="#00FF9F" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Expert Advice */}
          <div className="glass-panel-heavy p-8 rounded-lg neon-border" data-testid="expert-advice">
            <div className="flex items-start gap-6">
              <div className="shrink-0">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 p-1">
                  <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-3xl">
                    💡
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold font-['Orbitron'] text-cyan-400 mb-3">
                  Conseil d'Expert
                </h3>
                <p className="text-lg text-white mb-4 leading-relaxed">
                  {simulation.expert_advice}
                </p>
                <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <div className="text-sm text-slate-400 mb-1">Stratégie recommandée</div>
                  <div className="text-lg font-semibold text-cyan-400">
                    {simulation.recommended_strategy}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
